import { createFileRoute, useNavigate, redirect } from '@tanstack/react-router';
import { useState } from 'react';
import { toast } from '../utils/toast';
import { extractFieldErrors, type FieldErrors } from '../utils/apiError';
import { FiArrowLeft } from 'react-icons/fi';
import { Header } from '../components/layout/Header';
import { BottomNav } from '../components/layout/BottomNav';
import { ServiceForm, type ServiceFormValues } from '../components/services/ServiceForm';
import { useCreateService } from '../api/services';
import { uploadFilesToCloudinary } from '../utils/cloudinary';
import { normalizeServicePriceValue } from '../utils/price';

import { ProfileGuard } from '../components/auth/ProfileGuard';

export const Route = createFileRoute('/services/create')({
  beforeLoad: ({ location }) => {
    if (!localStorage.getItem('access_token')) {
      throw redirect({ to: '/sign-in', search: { redirect: location.pathname } });
    }
  },
  component: CreateServicePage,
});

function CreateServicePage() {
  const navigate = useNavigate();
  const [isUploading, setIsUploading] = useState(false);
  const [serverErrors, setServerErrors] = useState<FieldErrors>({});
  const createService = useCreateService();

  const handleSubmit = async (values: ServiceFormValues) => {
    setIsUploading(true);
    setServerErrors({}); // Clear previous server errors

    // Upload images in user-defined order, preserving drag-sorted sequence
    let finalImages: string[];
    try {
      finalImages = await Promise.all(
        values.orderedImageEntries.map(async (entry) => {
          if (entry.type === 'existing') return entry.url;
          const [result] = await uploadFilesToCloudinary([entry.file], {
            folder: 'service_images',
            uploadPreset: 'service_preset',
          });
          return result.secure_url;
        }),
      );
    } catch {
      toast.error('Image upload failed. Please try again.');
      setIsUploading(false);
      return;
    }

    try {
      const normalizedPrice = normalizeServicePriceValue(values.price);
      await createService.mutateAsync({
        title: values.title,
        description: values.description,
        price: normalizedPrice || undefined,
        images: finalImages,
        work_example_links: values.workExampleLinks.filter(Boolean),
        user_types: values.userTypeIds,
        tags: values.tagIds,
        alternate_email: values.alternateEmail || undefined,
      });
      navigate({ to: '/services' });
    } catch (err) {
      // Extract field-specific errors from API response
      const fieldErrors = extractFieldErrors(err);
      if (Object.keys(fieldErrors).length > 0) {
        setServerErrors(fieldErrors);
      }
      // Error toast is already shown by the mutation's onError handler
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white pb-32">
      <Header />

      <ProfileGuard>
        <main className="pt-24 md:pt-20">
          <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 mb-2">
            <button
              onClick={() => window.history.back()}
              className="p-2 hover:bg-white/5 rounded-full transition-colors group"
            >
              <FiArrowLeft
                size={24}
                className="text-zinc-400 group-hover:text-white transition-colors"
              />
            </button>
          </div>

          <ServiceForm
            title="New Service"
            submitLabel="Create Service"
            onSubmit={handleSubmit}
            isLoading={isUploading || createService.isPending}
            serverErrors={serverErrors}
          />
        </main>
      </ProfileGuard>

      <BottomNav />
    </div>
  );
}
